/**
 * 外出モード（前景セッション計測、§7.2）。
 * watchPositionを1分に間引いて積分ループへ。タブ非表示はバナーで明示し、
 * 復帰時の間隙はエンジンの欠測規則（§7.3）が安全側で補完する。
 */
import { AUTO_START_DISTANCE_M, SESSION_MAX_S, tick, TICK_SECONDS } from "@uvk/shared";
import type { GpsFix } from "@uvk/shared";
import {
  activeArea,
  adherenceSpfLabel,
  jstHour,
  maybeSwitchArea,
  notify,
  persistDay,
  rolloverIfNeeded,
  state,
} from "./state.js";

/** 画面上部のバナーに一時的なお知らせを出す（計測を止めずに伝えるため） */
function announce(message: string): void {
  const banner = document.getElementById("banner");
  if (!banner) return;
  banner.textContent = message;
  banner.hidden = false;
  setTimeout(() => (banner.hidden = true), 8000);
}
import { ensureWx, wxAt } from "./wxService.js";
import { ensureTerrain, terrainCellAt } from "./terrainService.js";
import { pruneTrackPoints, saveFix } from "./db.js";

let watchId: number | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let latestFix: GpsFix | null = null;
let lastTickConsumedFix = false;

function onPosition(p: GeolocationPosition): void {
  latestFix = {
    ts: p.timestamp,
    lat: p.coords.latitude,
    lon: p.coords.longitude,
    acc: p.coords.accuracy,
  };
  lastTickConsumedFix = false;
}

async function runTick(): Promise<void> {
  const now = Date.now();
  await rolloverIfNeeded(now);

  // セッション上限8時間（バッテリー保護、§7.2）
  if (state.sessionStartedAt && now - state.sessionStartedAt > SESSION_MAX_S * 1000) {
    await stopSession();
    return;
  }

  // 直近2分より古いfixは欠測扱い（§7.3の補完規則に落とす）
  const fix = latestFix && now - latestFix.ts <= 2 * TICK_SECONDS * 1000 && !lastTickConsumedFix
    ? latestFix
    : null;
  if (latestFix) lastTickConsumedFix = true;

  const pos = fix ?? state.engine.lastFix;
  // 測位できたら、そこが別エリアなら自動で切り替える（計測は止めない）
  if (fix && (await maybeSwitchArea(fix))) {
    announce(`現在地に合わせて地形データを「${activeArea().name}」に切り替えました`);
  }
  const area = activeArea();
  if (pos) {
    await ensureWx(pos, now);
    await ensureTerrain(state.settings.heightMode, area.id);
  }
  const wx = pos
    ? wxAt(pos, now)
    : { uvi: 0, uviClearSky: 0, fdBb: 1, cmf: 0, tempC: 25, rh: 60, pm25: 0, degraded: false };
  state.degraded = wx.degraded;

  const cell = pos ? terrainCellAt(pos, state.settings.heightMode, area.id) : null;
  const r = tick(state.engine, { now, fix, wx, cell, hourJst: jstHour(now) }, {
    settings: state.settings,
    adherenceSpfLabel: adherenceSpfLabel(),
  });
  state.lastDetail = r.detail;

  for (const a of r.alerts) {
    state.alertsLog.unshift(a);
    showLocalNotification(a.message);
  }
  state.alertsLog = state.alertsLog.slice(0, 30);

  if (fix) await saveFix(fix);
  notify();
}

function showLocalNotification(body: string): void {
  if (typeof Notification !== "undefined" && Notification.permission === "granted") {
    navigator.serviceWorker?.ready
      .then((reg) => reg.showNotification("UVかけいぼ", { body, icon: "./icon.svg", tag: "uvk-rt" }))
      .catch(() => {});
  }
}

export async function startSession(): Promise<void> {
  if (state.sessionActive) return;
  if (!("geolocation" in navigator)) {
    alert("この端末では位置情報が使えません");
    return;
  }
  if (typeof Notification !== "undefined" && Notification.permission === "default") {
    await Notification.requestPermission().catch(() => {});
  }
  watchId = navigator.geolocation.watchPosition(onPosition, () => {}, {
    enableHighAccuracy: true,
    maximumAge: 30_000,
  });
  state.sessionActive = true;
  state.sessionStartedAt = Date.now();
  await runTick(); // 開始直後に1tick
  timer = setInterval(() => void runTick(), TICK_SECONDS * 1000);
  await pruneTrackPoints(Date.now());
  notify();
}

export async function stopSession(): Promise<void> {
  if (watchId !== null) navigator.geolocation.clearWatch(watchId);
  if (timer !== null) clearInterval(timer);
  watchId = null;
  timer = null;
  state.sessionActive = false;
  state.sessionStartedAt = null;
  await persistDay(); // 終了時に日次集計を保存・同期
  notify();
}

/** タブ非表示で計測が止まる場合の明示バナー（§7.2） */
export function watchVisibility(banner: HTMLElement): void {
  document.addEventListener("visibilitychange", () => {
    if (document.hidden && state.sessionActive) {
      banner.textContent =
        "画面を閉じると計測が一時停止します（復帰時は安全側で補完します）";
      banner.hidden = false;
    } else {
      banner.hidden = true;
      // アプリに戻ってきたタイミングでも自宅チェック（PWAは背景監視不可のため）
      void maybeAutoStart(banner);
    }
  });
}

function distanceM(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const mLat = 111320;
  const mLon = 111320 * Math.cos((a.lat * Math.PI) / 180);
  return Math.hypot((a.lat - b.lat) * mLat, (a.lon - b.lon) * mLon);
}

/**
 * 自動外出モード：自宅登録＋自動開始ONのとき、アプリを開いた時点で
 * 自宅から AUTO_START_DISTANCE_M 以上離れていたら計測を自動開始する。
 * ※PWAはアプリを閉じた状態での位置監視ができない（§7.2）ため、
 *   「家を出たら自動ON」はアプリが前景になった時点での判定。完全な
 *   バックグラウンド化はCapacitorネイティブ殻（第2段）で対応。
 */
export async function maybeAutoStart(banner?: HTMLElement): Promise<void> {
  const { home, autoStart } = state.localPrefs;
  if (!autoStart || !home || state.sessionActive) return;
  if (!("geolocation" in navigator)) return;

  const pos = await new Promise<GeolocationPosition | null>((resolve) => {
    navigator.geolocation.getCurrentPosition(resolve, () => resolve(null), {
      maximumAge: 60_000,
      timeout: 10_000,
    });
  });
  if (!pos) return;
  const here = { lat: pos.coords.latitude, lon: pos.coords.longitude };
  if (distanceM(here, home) >= AUTO_START_DISTANCE_M) {
    await startSession();
    if (banner) {
      banner.textContent = "自宅から離れているため、外出モードを自動開始しました";
      banner.hidden = false;
      setTimeout(() => (banner.hidden = true), 8000);
    }
  }
}
